import { useState, useEffect, useCallback } from 'react';
import { FileText, CheckCircle, XCircle, Clock, Loader2, ClipboardList, Search, X, CalendarDays, User, ClipboardCheck } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { toastMessage } from '@/utils/toast-message';
import MultiClassSelect, { CurriculumGroup, ClassFilterChips } from '@/components/common/MultiClassSelect';

const BASE = '/api/parent-app/admin';

type LeaveStatus = 'pending' | 'approved' | 'rejected';

interface LeaveRequest {
  id: number;
  from_date: string;
  to_date: string;
  reason: string;
  status: LeaveStatus;
  admin_note: string | null;
  reviewed_at: string | null;
  created_at: string;
  student_name: string;
  admission_number: string | null;
  class_name: string | null;
  section_name: string | null;
  parent_name: string;
  parent_phone: string | null;
}

const STATUS_CONFIG: Record<LeaveStatus, { label: string; color: string; icon: React.ReactNode }> = {
  pending:  { label: 'Pending',  color: '#f59e0b', icon: <Clock      size={13} /> },
  approved: { label: 'Approved', color: '#22c55e', icon: <CheckCircle size={13} /> },
  rejected: { label: 'Rejected', color: '#ef4444', icon: <XCircle    size={13} /> },
};

function fmt(d: string) {
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function dayDiff(from: string, to: string) {
  const diff = (new Date(to + 'T00:00:00').getTime() - new Date(from + 'T00:00:00').getTime()) / 86400000 + 1;
  return diff === 1 ? '1 day' : `${diff} days`;
}

export default function LeavePage() {
  const [tab, setTab]         = useState<LeaveStatus | 'all'>('pending');
  const [items, setItems]     = useState<LeaveRequest[]>([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]           = useState('');
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [classGroups, setClassGroups] = useState<CurriculumGroup[]>([]);
  const [reviewing, setReviewing] = useState<LeaveRequest | null>(null);
  const [note, setNote]       = useState('');
  const [acting, setActing]   = useState(false);

  const LIMIT = 20;

  useEffect(() => {
    apiRequest<any>(`${BASE}/classes`).then(res => {
      setClassGroups(res?.data?.grouped ?? []);
    });
  }, []);

  const load = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ status: tab, page: String(pg), limit: String(LIMIT) });
      if (search) qs.set('search', search);
      selectedClasses.forEach(id => qs.append('class_id', id));
      const res = await apiRequest<{ data: LeaveRequest[]; total: number }>(`${BASE}/leave-requests?${qs}`);
      setItems(res.data?.data ?? []);
      setTotal(res.data?.total ?? 0);
      setPage(pg);
    } catch { toastMessage('Failed to load', 'error'); }
    finally { setLoading(false); }
  }, [tab, search, selectedClasses]);

  useEffect(() => { load(1); }, [load]);

  const handleAction = async (status: 'approved' | 'rejected') => {
    if (!reviewing) return;
    setActing(true);
    try {
      const res = await apiRequest(`${BASE}/leave-requests/${reviewing.id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status, admin_note: note.trim() || null }),
      });
      if (!res.success) return toastMessage(res.message || 'Failed to update', 'error');
      toastMessage(status === 'approved' ? 'Leave approved' : 'Leave rejected');
      setReviewing(null);
      setNote('');
      load(page);
    } catch { toastMessage('Failed', 'error'); }
    finally { setActing(false); }
  };

  const openReview = (item: LeaveRequest) => {
    setReviewing(item);
    setNote('');
  };

  const TABS: { value: LeaveStatus | 'all'; label: string }[] = [
    { value: 'pending',  label: 'Pending'  },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'all',      label: 'All'      },
  ];

  return (
    <>
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)' }}>
      {/* Page Header */}
      <div className="page-head">
        <div className="page-head-icon">
          <FileText className="w-6 h-6" />
        </div>
        <div className="page-head-text">
          <h1>Leave Requests</h1>
          <p>Approve or reject parent leave applications</p>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '24px 32px', gap: 16, overflow: 'hidden', minHeight: 0 }}>

      {/* Search + Tabs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }} />
          <input
            className="input"
            style={{ paddingLeft: 36, width: 260 }}
            placeholder="Search by student"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <MultiClassSelect
          classGroups={classGroups}
          selectedIds={selectedClasses}
          onChange={ids => { setSelectedClasses(ids); }}
        />
        <div className="seg" style={{ marginLeft: 'auto', alignSelf: 'flex-start' }}>
          {TABS.map(t => (
            <button
              key={t.value}
              className={tab === t.value ? 'active' : ''}
              onClick={() => { setTab(t.value); setReviewing(null); }}
            >{t.label}</button>
          ))}
        </div>
      </div>

      <ClassFilterChips classGroups={classGroups} selectedIds={selectedClasses} onChange={setSelectedClasses} />

      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
      {loading ? (
        <div className="center" style={{ padding: '64px 20px' }}>
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--accent-deep)' }} />
        </div>
      ) : items.length === 0 ? (
        <div className="empty">
          <div className="empty-art">
            <ClipboardList size={36} />
          </div>
          <h3>No {tab === 'all' ? '' : tab} leave requests</h3>
          <p>{tab === 'pending' ? 'All caught up — no pending requests.' : 'Nothing to show here.'}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 8 }}>
          {items.map(item => {
            const s = STATUS_CONFIG[item.status];
            return (
              <div key={item.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Row 1: name + class */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{item.student_name}</span>
                    {item.class_name && (
                      <span style={{ fontSize: 11, color: 'var(--text-3)', background: 'var(--surface-2)', borderRadius: 5, padding: '2px 6px' }}>
                        {item.class_name}{item.section_name ? ` · ${item.section_name}` : ''}
                      </span>
                    )}
                  </div>
                  {/* Row 2: date + parent inline */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-2)' }}>
                      <CalendarDays size={12} style={{ color: 'var(--accent-deep)', flexShrink: 0 }} />
                      {fmt(item.from_date)} – {fmt(item.to_date)}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-2)' }}>
                      <Clock size={12} style={{ color: 'var(--accent-deep)', flexShrink: 0 }} />
                      <span style={{ fontWeight: 600, color: 'var(--text)' }}>{dayDiff(item.from_date, item.to_date)}</span>
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-2)' }}>
                      <User size={12} style={{ color: 'var(--accent-deep)', flexShrink: 0 }} />
                      {item.parent_name}{item.parent_phone ? ` · ${item.parent_phone}` : ''}
                    </span>
                  </div>
                </div>

                {/* Right side */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: s.color, background: `${s.color}18`, borderRadius: 7, padding: '4px 10px' }}>
                    {s.icon} {s.label}
                  </div>
                  {item.status === 'pending' && (
                    <button className="btn btn-primary" style={{ fontSize: 11, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4 }} onClick={() => openReview(item)}>
                      <ClipboardCheck size={13} /> Review Request
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {total > LIMIT && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          <button className="btn btn-secondary" disabled={page === 1} onClick={() => load(page - 1)}>Previous</button>
          <span style={{ lineHeight: '36px', fontSize: 13, color: 'var(--text-2)' }}>Page {page} of {Math.ceil(total / LIMIT)}</span>
          <button className="btn btn-secondary" disabled={page * LIMIT >= total} onClick={() => load(page + 1)}>Next</button>
        </div>
      )}
      </div>
      </div>
    </div>

    {/* Review Modal */}
    {reviewing && (
      <>
        <div className="scrim" onClick={() => { setReviewing(null); setNote(''); }} />
        <div className="modal">
          <div className="modal-head">
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--accent-wash)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <ClipboardList size={17} style={{ color: 'var(--accent-deep)' }} />
            </div>
            <div style={{ flex: 1 }}>
              <h2>Review Leave Request</h2>
              <p>{reviewing.student_name} · {fmt(reviewing.from_date)} – {fmt(reviewing.to_date)} · {dayDiff(reviewing.from_date, reviewing.to_date)}</p>
            </div>
            <button className="icon-btn" onClick={() => { setReviewing(null); setNote(''); }}>
              <X size={17} />
            </button>
          </div>

          <div className="modal-body col gap-16">
            <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Reason</div>
              <div style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.6 }}>{reviewing.reason}</div>
            </div>
            <div className="field">
              <label>Note to Parent <span className="opt">(optional)</span></label>
              <textarea
                className="textarea"
                rows={3}
                placeholder="Add a note for the parent…"
                value={note}
                onChange={e => setNote(e.target.value)}
                style={{ resize: 'none' }}
                autoFocus
              />
            </div>
          </div>

          <div className="modal-foot">
            <button className="btn btn-secondary" onClick={() => { setReviewing(null); setNote(''); }} disabled={acting}>Cancel</button>
            <button
              disabled={acting}
              onClick={() => handleAction('rejected')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 'var(--r-btn)', border: 'none', background: '#fef2f2', color: '#ef4444', fontWeight: 700, cursor: 'pointer', fontSize: 13, minWidth: 90 }}
            >
              <XCircle size={14} /> {acting ? '…' : 'Reject'}
            </button>
            <button className="btn btn-primary" onClick={() => handleAction('approved')} disabled={acting} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 100 }}>
              <CheckCircle size={14} /> {acting ? '…' : 'Approve'}
            </button>
          </div>
        </div>
      </>
    )}
    </>
  );
}
